import { InfoFieldsBuilderInterface } from "../common/InfoFieldsBuilder";
import {cmToFeetAndInches, formatDateFromISOString, kgToLbs} from '../../../src/common/utils/hashUtil'
import {
  ClinicalInfoInterface,
  DemographicInfoInterface,
} from "../../types/PatientData.interface";
type PatientData = ClinicalInfoInterface & DemographicInfoInterface;

export const getHeaderFields = (
  data: PatientData
): InfoFieldsBuilderInterface[] => [
  {
    title: "Phone number",
    textArray: [data?.phoneNumber ? (data?.phoneNumber.includes("+") ? data?.phoneNumber : "+"+data?.phoneNumber) : ""],
    colWidth: "col-sm-3",
  },
  {
    title: "Medical ID number",
    textArray: [data?.identityNumber ? data.identityNumber : ""],
    colWidth: "col-sm-3",
  },
  {
    title: "DOB",
    textArray: [data?.birthday ? formatDateFromISOString(data?.birthday) : ""],
    colWidth: "col-sm-3",
  },
  {
    title: "Last visit Date",
    textArray: [data?.lastVisit ? formatDateFromISOString(data?.lastVisit) : "N/A"],
    colWidth: "col-sm-3",
  },
];

export const getDemographicFields = (
  data: PatientData
): InfoFieldsBuilderInterface[] => [
  {
    title: "Email",
    textArray: [data?.email ?? ""],
    colWidth: "col-sm-3",
  },
  {
    title: "Gender",
    textArray: [data?.gender ?? ""],
    colWidth: "col-sm-3",
  },
  {
    title: "Marital Status",
    textArray: [data?.maritalStatus ?? ""],
    colWidth: "col-sm-3",
  },
  {
    title: "Race / Ethnicity",
    textArray: [data?.race ?? ""],
    colWidth: "col-sm-3",
  },
  {
    title: "Address",
    textArray: [
      `${data?.address ?? ""} ${data?.city ?? ""} ${data?.zip ?? ""}`,
    ],
    fullRow: true,
  },
];

const getClinicalOneFields = (
  data: PatientData
): InfoFieldsBuilderInterface[] => [
  {
    title: "Symptomatic",
    textArray: [data.isSymptomatic === undefined ? "N/A" : data.isSymptomatic ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "Symptom type",
    textArray: data?.symptomsType === undefined ? ["N/A"] : [data?.symptomsType ?? "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "Symptoms",
    textArray: (() => {
      // List only the available symptoms
      const symptoms: string[] = [];

      if (data.chestPainType) {
        const chestPainLabel =
          data.chestPainType === "Typical"
            ? "Typical chest pain"
            : data.chestPainType === "Atypical"
            ? "Atypical chest pain"
            : undefined;

        if (chestPainLabel) {
          symptoms.push(chestPainLabel);
        }
      }
      if (data.shortnessOfBreath) {
        symptoms.push("Shortness of breath");
      }
      if (data.shoulderOrArmPain) {
        symptoms.push("Shoulder or arm pain");
      }
      if (data.jawPain) {
        symptoms.push("Jaw pain");
      }
      if (data.dizzinessOrPresyncope) {
        symptoms.push("Dizziness or pre-syncope");
      }
      if (data.nausea) {
        symptoms.push("Nausea");
      }

      return symptoms.length > 0 ? [symptoms.join(" / ")] : ["N/A"];
    })(),
    colWidth: "col-sm-3",
  },
  {
    title: "BMI",
    //this calc belongs in a submethod calculateBMI()
    textArray: [data?.bmi?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
  },
  {
    title: "Smoking",
    textArray: [data?.smokingStatus === undefined ? "N/A" : data?.smokingStatus ?? ""],
    colWidth: "col-sm-3",
  },
  {
    title: "BP",
    textArray: data?.bpSystolic === undefined || data?.bpDiastolic === undefined ? ["N/A"] : [`${data?.bpSystolic ?? ""} / ${data?.bpDiastolic ?? ""}`],
    colWidth: "col-sm-3",
    suffix: "mmHg",
  },
  {
    title: "COPD",
    textArray: [data.copd === undefined ? "N/A" : data.copd ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "Total Cholesterol",
    textArray: [data?.totalCholesterol?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    suffix: "mg/dL",
  },
  {
    title: "LDL",
    textArray: [data?.ldl?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    suffix: "mg/dL",
  },
  {
    title: "Triglycerides",
    textArray: [data?.triglycerides?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    suffix: "mg/dL",
  },
  {
    title: "Sugar",
    textArray: [data?.sugarLevel?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    suffix: "mg/dL",
  },
  {
    title: "Arrhtimias / conduction abnormalities",
    textArray: [data.arrhythmiasOrConductionAbnormality === undefined ? "N/A" : data.arrhythmiasOrConductionAbnormality ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "Cardiac family history",
    textArray: [data.familyHistoryCardiovascularEvents === undefined ? "N/A" : data.familyHistoryCardiovascularEvents ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "CHF",
    textArray: [data.chronicHeartFailure === undefined ? "N/A" : data.chronicHeartFailure ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "LVEF <30%",
    textArray: [data.lvefLessThan30 === undefined ? "N/A" : data.lvefLessThan30 ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "PAD",
    textArray: [data.peripheralArterialDisease === undefined ? "N/A" : data.peripheralArterialDisease ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "Pulmonary edema",
    textArray: [data.pulmonaryEdema === undefined ? "N/A" : data.pulmonaryEdema ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  {
    title: "Anemia",
    textArray: [data.anemia === undefined ? "N/A" : data.anemia ? "Yes" : "No"],
    colWidth: "col-sm-3",
  },
  // add the weight and height fields
  {
    title: "Weight",
    textArray: [data?.weight?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    checkMetric:true,
    metric:'metric',
    suffix: "kg",
  },
  {
    title: "Height",
    textArray: [data?.height?.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    checkMetric:true,
    metric:'metric',
    suffix: "cm",
  },
  {
    title:"Height (Inch)",
    textArray:[cmToFeetAndInches(data?.height).inches.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    checkMetric:true,
    metric:'imperial',
    suffix: "in",
  },
  {
    title:"Height (Feet)",
    textArray:[cmToFeetAndInches(data?.height).feet.toString() ?? "N/A"],
    colWidth: "col-sm-3",
    checkMetric:true,
    metric:'imperial',
    suffix: "ft",
  }
  ,
  {
    title:"Weight (Pounds)",
    textArray:[kgToLbs(data?.weight).toString() ?? "N/A"],
    colWidth: "col-sm-3",
    checkMetric:true,
    metric:'imperial',
    suffix: "lbs",
  }
];

const getClinicalTwoFields = (
  data: PatientData
): InfoFieldsBuilderInterface[] => [
  {
    title: "Cerebrovascular Disease ",
    textArray: [data.cerebrovascularDisease === undefined ? "N/A" : data.cerebrovascularDisease ? "Yes" : "No"],
    colWidth: "col-3",
  },
  {
    title: "Stroke / TIA within 6 months",
    textArray: [data.strokeOrTiaOrRIND === undefined ? "N/A" : data.strokeOrTiaOrRIND ? "Yes" : "No"],
    colWidth: "col-3",
  },
  {
    title: "Hemodynamic Instability",
    textArray: [data.hemodynamicInstability === undefined ? "N/A" : data.hemodynamicInstability   ? "Yes" : "No"],
    colWidth: "col-3",
  },
  {
    title: "Medications",
    textArray: [data.takingOtherDrugs === undefined ? "N/A" : data.takingOtherDrugs ? "Yes" : "No"],
    colWidth: "col-3",
  },
  {
    title: "Notes",
    textArray: [data?.notes === undefined ? "N/A" : data?.notes ?? ""],
    colWidth: "col-12",
  },
  {
    title:"Other Co-Morbidities",
    textArray: [data?.otherCoMorbidities === undefined ? "N/A" : data?.otherCoMorbidities ?? ""],
    colWidth: "col-12",
  }
];

export const getPatientInfoFields = (data: PatientData) => ({
  HEADER: getHeaderFields(data),
  DEMOGRAPHIC: getDemographicFields(data),
  CLINICAL_ONE: getClinicalOneFields(data),
  CLINICAL_TWO: getClinicalTwoFields(data),
});
